/* eslint-disable import/no-cycle */
import moment from 'moment';
import { get, compact, isEmpty, uniq, isNumber } from 'lodash';
import {
  DEFAULT_ERROR,
  DEFAULT_ITEMS_PER_PAGE,
  DATE_PICKER_FORMAT,
  DEFAULT_PAGE,
} from '../utils/constants';
import DatadogHandler from '../utils/datadog';
import {
  classService,
  MS_PHASE_3_ENDPOINTS,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
} from '../utils/msEndpointConstants';
import {
  convertObjToSnakeCase,
  retrieveListOrObject,
  convertObjToCamelCase,
  retrieveSortForMS,
} from '../utils';

import { transformGetAllClassInfo } from '../utils/ms-utils/ms-phase-3/ClassUtils';
import { getLevelByIds, getNextLevelsByLevelIds } from './SchoolConfigService';
import { getCentreByIds } from './CentreService';
import {
  getChildByIds,
  getChildCurrentClasses,
  getChildUpcomingClasses,
  getChildUpcomingClassesByChildIds,
} from './ChildService';
import { getUserByIds } from './UserService';

const fileName = 'src/services/ClassService.js';

const { CLASS } = MS_PHASE_3_ENDPOINTS;
const { CLASS: CLASS_P2B } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;

export const getClassByIds = (classIds = []) => {
  if (classIds.length === 0) return [];
  return classService()
    .post(...CLASS_P2B.GET_CLASS_BY_IDS(classIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getClassTeachersByClassIds = async (classIds = []) => {
  if (classIds.length === 0) return [];
  return classService()
    .post(...CLASS_P2B.GET_CLASS_TEACHERS_BY_CLASS_IDS(classIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getClassLevelByClassIds = async (classIds = []) => {
  if (classIds.length === 0) return {};
  return classService()
    .post(...CLASS_P2B.GET_CLASS_LEVEL_BY_CLASS_IDS(classIds))
    .then(res => get(res, 'data.data', {}))
    .catch(() => ({}));
};

export const getAllClassInfo = async reqBody => {
  try {
    const filter = get(reqBody, 'filter', {});
    const pagination = get(reqBody, 'pagination', {});
    const classSvc = classService();
    const params = {
      page: get(pagination, 'page', 1),
      perPage: get(pagination, 'perPage', DEFAULT_ITEMS_PER_PAGE),
      ...retrieveSortForMS(pagination.sort, 'sort'),
    };

    const body = Object.assign(
      {},
      get(reqBody, 'fkSchool') && { school_id: get(reqBody, 'fkSchool') },
      get(reqBody, 'isDefault') && {
        is_default: get(reqBody, 'isDefault'),
      },
      get(reqBody, 'isHidden') && { is_hidden: get(reqBody, 'isHidden') },
      get(reqBody, 'onlyCurrentClass') && {
        only_current_class: get(reqBody, 'onlyCurrentClass'),
      },
      !isEmpty(filter) && {
        filter: {
          ...((isNumber(filter?.fkCentre) || filter?.fkCentre?.length) && {
            centre_id: [
              {
                eq: filter?.fkCentre,
              },
            ],
          }),
          ...((isNumber(filter?.fkLevel) || filter?.fkLevel?.length) && {
            level_id: [
              {
                eq: filter?.fkLevel,
              },
            ],
          }),
          ...(filter?.isHidden !== undefined && {
            is_hidden: [{ eq: filter?.isHidden }],
          }),
          ...(filter?.ID && {
            id: [{ eq: filter?.ID }],
          }),
        },
      }
    );
    return classSvc.post(...CLASS.getAllClassInfo(body, params));
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getAllClassInfoClassList',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllClassManagementList = async (_, reqBody) => {
  try {
    const response = await getAllClassInfo(reqBody);
    const levelIds = uniq(
      compact(get(response, 'data.data', []).map(item => item?.fk_level))
    );
    const centreIds = uniq(
      compact(get(response, 'data.data', []).map(item => item?.fk_centre))
    );

    const classIds = uniq(
      compact(get(response, 'data.data', []).map(item => item?.id))
    );

    const [
      levels,
      nextLevels,
      centres,
      childCurrentClasses,
      childUpcomingClasses,
      classTeachers,
    ] = await Promise.all([
      getLevelByIds(levelIds),
      getNextLevelsByLevelIds(levelIds),
      getCentreByIds(centreIds),
      getChildCurrentClasses({
        class_ids: classIds,
      }),
      getChildUpcomingClasses({
        class_ids: classIds,
      }),
      getClassTeachersByClassIds(classIds),
    ]);

    const teacherIds = uniq(
      compact(classTeachers?.map(item => item?.fk_teacher))
    );

    const teachers = await getUserByIds(teacherIds);

    return transformGetAllClassInfo({
      response,
      levels,
      nextLevels,
      centres,
      childCurrentClasses,
      classTeachers,
      teachers,
      childUpcomingClasses,
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllClassInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllClassInfoClassDetails = async (_, reqBody) => {
  try {
    const response = await getAllClassInfo(reqBody);
    const classIds = uniq(
      compact(get(response, 'data.data', []).map(item => item?.id))
    );

    const [childCurrentClasses, childUpcomingClasses, classTeachers] =
      await Promise.all([
        getChildCurrentClasses({
          class_ids: classIds,
          alphabetical_sort: true,
        }),
        getChildUpcomingClasses({
          class_ids: classIds,
          alphabetical_sort: true,
        }),
        getClassTeachersByClassIds(classIds),
      ]);

    const teacherIds = uniq(
      compact(classTeachers?.map(item => item?.fk_teacher))
    );

    const childIds = uniq(
      [
        ...compact(
          Object.values(childCurrentClasses || {})
            .map(item => item?.data || [])
            .flat()
        ),
        ...compact(
          Object.values(childUpcomingClasses || {})
            .map(item => item?.data || [])
            .flat()
        ),
      ]?.map(item => item?.fk_child)
    );

    const [teachers, children, childUpcomingClassesByChildIds] =
      await Promise.all([
        getUserByIds(teacherIds),
        getChildByIds(childIds),
        getChildUpcomingClassesByChildIds({
          child_class_filter: {
            child_id: [{ eq: childIds }],
          },
        }),
      ]);

    const classes = await getClassByIds([
      ...classIds,
      ...compact(
        Object.values(childUpcomingClassesByChildIds || {})
          .map(item => item?.data || [])
          .flat()
      ).map(item => item?.fk_class),
    ]);

    const levelIds = uniq([
      ...compact(get(response, 'data.data', []).map(item => item?.fk_level)),
      ...compact(classes.map(item => item?.fk_level)),
    ]);
    const centreIds = uniq([
      ...compact(get(response, 'data.data', []).map(item => item?.fk_centre)),
      ...compact(classes.map(item => item?.fk_centre)),
    ]);

    const [levels, nextLevels, centres] = await Promise.all([
      getLevelByIds(levelIds),
      getNextLevelsByLevelIds(levelIds),
      getCentreByIds(centreIds),
    ]);

    return transformGetAllClassInfo({
      response,
      levels,
      nextLevels,
      centres,
      childCurrentClasses,
      classTeachers,
      teachers,
      childUpcomingClasses,
      children,
      childUpcomingClassesByChildIds,
      classes,
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllClassInfoClassDetails',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msGetAllClassInfoClassList = async (_, reqBody) => {
  try {
    const response = await getAllClassInfo(reqBody);
    const levelIds = uniq(
      compact(get(response, 'data.data', []).map(item => item?.fk_level))
    );
    const centreIds = uniq(
      compact(get(response, 'data.data', []).map(item => item?.fk_centre))
    );

    const [levels, nextLevels, centres] = await Promise.all([
      getLevelByIds(levelIds),
      getNextLevelsByLevelIds(levelIds),
      getCentreByIds(centreIds),
    ]);

    return transformGetAllClassInfo({
      response,
      levels,
      nextLevels,
      centres,
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllClassInfoClassList',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateClassDetails = async (_, body = {}) => {
  const { classId, teacherList, updateClass } = body;
  try {
    const resp = await classService().put(CLASS.updateClassDetails(classId), {
      teacher_list: teacherList,
      update_class: {
        cover_photo_key: get(updateClass, 'coverPhotoKey'),
        description: get(updateClass, 'description'),
        label: get(updateClass, 'label'),
        is_default: get(updateClass, 'isDefault'),
        is_hidden: get(updateClass, 'isHidden'),
        profile_photo_key: get(updateClass, 'profilePhotoKey'),
      },
    });
    const data = get(resp, 'data', {});
    return {
      success: true,
      data: {
        updateClassDetails: {
          ID: get(data, 'id'),
          label: get(data, 'label'),
          description: get(data, 'description'),
          coverPhotoKey: get(data, 'cover_photo_key'),
          coverPhotoKeyPresignedURL: get(data, 'cover_photo_key_presigned_url'),
          profilePhotoKey: get(data, 'profile_photo_key'),
          profilePhotoKeyPresignedURL: get(
            data,
            'profile_photo_key_presigned_url'
          ),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateClassDetails',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msGetClassPhotoUploadUrl = async (_, reqData) => {
  try {
    const classSvc = classService();
    const resp = await classSvc.post(
      CLASS.getClassPhotoUploadUrl,
      convertObjToSnakeCase(reqData)
    );
    const data = resp?.data || {};
    return {
      data: {
        getClassPhotoUploadURL: {
          Filename: data?.file_name,
          Url: data?.url,
          Key: data?.key,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetClassPhotoUploadUrl',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(ex, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msUploadMeasurementFile = async (_, reqData) => {
  try {
    const { classID, level, file } = reqData;
    const classSvc = classService();
    const payload = {
      class_id: classID,
      level_id: level,
      file: {
        file_name: get(file, 'filename'),
        file_size: get(file, 'filesize'),
        mime_type: get(file, 'mimetype'),
        ...(get(file, 'file_path') && { file_path: get(file, 'file_path') }),
      },
    };
    const resp = await classSvc.post(
      CLASS.getClassMeasurementUploadUrl,
      payload
    );
    const data = resp?.data || {};
    return {
      data: {
        getClassMeasurementUploadURL: {
          Filename: data?.file_name,
          Url: data?.url,
          Key: data?.key,
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUploadMeasurementFile',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(ex, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msCreateNewClass = async (_, reqData) => {
  try {
    const { newClass, teacherList } = reqData;
    const resp = await classService().post(CLASS.createNewClass, {
      class_detail: {
        description: get(newClass, 'description'),
        fk_centre: get(newClass, 'fkCentre'),
        fk_level: get(newClass, 'fkLevel'),
        is_default: get(newClass, 'isDefault', false),
        is_hidden: get(newClass, 'isHidden', false),
        label: get(newClass, 'label'),
      },
      teacher_list: teacherList,
    });
    return {
      data: {
        createNewClass: {
          ID: get(resp, 'data.id'),
        },
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCreateNewClass',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msGetAllClassInfoForChild = async (_, reqData) => {
  try {
    const { childID, effectiveDate, isHidden, pagination = {} } = reqData;
    const { page = DEFAULT_PAGE, perPage = DEFAULT_ITEMS_PER_PAGE } =
      pagination;

    const params = {
      page: parseInt(page, 10),
      perPage: parseInt(perPage, 10),
    };

    const formattedEffectiveDate = effectiveDate
      ? moment(effectiveDate).format(DATE_PICKER_FORMAT)
      : effectiveDate;

    const payload = convertObjToSnakeCase(
      {
        childID,
        effectiveDate: formattedEffectiveDate,
        isHidden,
      },
      [],
      ['child_id:childID']
    );

    const resp = await classService().post(
      ...CLASS.getAllClassInfoForChild(payload, params)
    );

    const responseData = get(resp, 'data', {});
    const totalCount = get(responseData, 'total_count', 0);
    const rawData = get(responseData, 'data', {});

    // Convert object with numeric keys to array, or keep array as is
    let dataArray = [];
    if (Array.isArray(rawData)) {
      dataArray = rawData;
    } else if (typeof rawData === 'object' && rawData !== null) {
      dataArray = Object.values(rawData);
    }

    const data = dataArray.map(item => ({
      ...item,
    }));

    return {
      success: true,
      data: {
        getAllClassInfoForChild: {
          totalCount,
          data,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllClassInfoForChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msGetAllClassByChildList = async (_, reqData) => {
  try {
    const { childIDs = [], effectiveDate, isHidden, pagination = {} } = reqData;

    const requests = childIDs.map(childID =>
      msGetAllClassInfoForChild(null, {
        childID,
        effectiveDate,
        isHidden,
        pagination,
      })
    );

    const results = await Promise.all(requests);

    const data = {};
    let hasError = false;
    const errors = [];

    results.forEach((result, index) => {
      const childID = childIDs[index];
      const queryKey = `getAllClassInfo${childID}`;

      if (result.success) {
        data[queryKey] = get(result, 'data.getAllClassInfoForChild', {
          totalCount: 0,
          data: [],
        });
      } else {
        hasError = true;
        errors.push(
          ...(get(result, 'error', []) || [
            { message: `Failed to fetch class info for child ${childID}` },
          ])
        );
        data[queryKey] = {
          totalCount: 0,
          data: [],
        };
      }
    });

    return {
      success: !hasError,
      data,
      ...(hasError && { error: errors }),
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msGetAllClassByChildList',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
      data: {},
    };
  }
};

export const msGetClassForPromotionChildList = async (
  _,
  { childList, fkSchool, onlyCurrentClass }
) => {
  const responses = await Promise.all(
    childList.map(async ({ childID, centre, level }) => {
      const res = await classService()
        .post(
          ...CLASS.getAllClassInfo({
            fk_school: fkSchool,
            only_current_class: onlyCurrentClass,
            filter: {
              centre_id: [
                {
                  eq: centre,
                },
              ],
              ...(level && {
                level_id: [
                  {
                    eq: level,
                  },
                ],
              }),
            },
          })
        )
        .catch(() => ({
          total_count: 0,
          data: [],
        }));

      return {
        [`getAllClassInfo${childID}`]: {
          totalCount: get(res, 'data.total_count', 0),
          data: (get(res, 'data.data', []) || []).map(item =>
            convertObjToCamelCase(item)
          ),
        },
      };
    })
  );
  return {
    success: true,
    data: Object.assign({}, ...responses),
  };
};
